import React, { useState } from 'react'
import { Alert, Button, CircularProgress } from '@mui/material'
import ContentCopyIcon from '@mui/icons-material/ContentCopy'
import { Link } from 'react-router-dom'
import { Dropzone, useGetBusiness } from '@marketing-milk/frontend'
import {
  CREATIVE_IMPORT_LIMITS,
  CREATIVE_IMPORT_PACKAGING_PROMPT,
} from '@marketing-milk/interfaces'
import { CreativeImportState } from '../state'
import { useCreativeImportActive } from '../useCreativeImportActive'

interface Props {
  state: CreativeImportState
  onFile: (file: File) => void
}

const MB = 1024 * 1024

export const UploadStep = ({ state, onFile }: Props) => {
  const business = useGetBusiness()
  const archiveErrors = state.report?.archiveErrors ?? []
  const [promptCopied, setPromptCopied] = useState(false)
  // Warn-but-allow: a running import is a hint the user may be re-uploading
  // the same ZIP by accident (duplicate drafts — no dedup), never a block.
  const activeImport = useCreativeImportActive(true).data

  const copyPrompt = async () => {
    await navigator.clipboard.writeText(CREATIVE_IMPORT_PACKAGING_PROMPT)
    setPromptCopied(true)
    setTimeout(() => setPromptCopied(false), 2500)
  }

  return (
    <div className="flex flex-col gap-4">
      {activeImport && (
        <Alert severity="warning">
          An import is already running for this business.{' '}
          <Link
            to={`/${business.id}/creatives/import/${activeImport.creativeImportID}`}
            className="underline"
          >
            View its progress
          </Link>{' '}
          — or continue below to start another one.
        </Alert>
      )}
      {state.error && <Alert severity="error">{state.error}</Alert>}
      {archiveErrors.map(error => (
        <Alert key={error.code} severity="error">
          {error.message}
        </Alert>
      ))}

      {state.validating ? (
        <div className="flex flex-col items-center py-12 gap-3">
          <CircularProgress />
          <p className="text-mm-neutral-1-600">Validating archive…</p>
        </div>
      ) : (
        <Dropzone
          allowedExtensions={['.zip']}
          inputProps={{ 'data-testid': 'creative-import-dropzone' }}
          handleUpload={(file: File) => onFile(file)}
        />
      )}

      <div className="rounded-lg border border-mm-neutral-1-200 bg-white p-4">
        <p className="font-medium text-mm-neutral-1-900">Package your ads with AI</p>
        <p className="mt-1 text-sm text-mm-neutral-1-600">
          Paste this prompt into the chat where you made the images. It writes the manifest, lays
          out the folder, and hands back the ZIP to drop above.
        </p>
        <Button
          size="small"
          variant="contained"
          startIcon={<ContentCopyIcon />}
          onClick={copyPrompt}
          className="mt-3"
        >
          {promptCopied ? 'Copied!' : 'Copy packaging prompt'}
        </Button>

        <p className="mt-4 text-sm text-mm-neutral-1-600">
          The manifest selects exactly which files become draft creatives — unlisted files are
          ignored. Copy fields (headline, description, …) are optional; drafts import incomplete and
          are finished in Builder.
        </p>

        <details className="mt-3 text-sm text-mm-neutral-1-600">
          <summary className="cursor-pointer select-none font-medium">
            Archive layout and limits
          </summary>
          <pre className="mt-2 overflow-x-auto rounded bg-mm-neutral-1-100 p-3">
            {
              'creatives.json       ← manifest listing the media to import\nmedia/\n  lion.png\n  cheetah.jpg\n  hunt.mp4'
            }
          </pre>
          <p className="mt-2">
            Up to {CREATIVE_IMPORT_LIMITS.maxRows} creatives and{' '}
            {CREATIVE_IMPORT_LIMITS.maxMediaTotal} files per import, .jpg/.png images up to{' '}
            {Math.round(CREATIVE_IMPORT_LIMITS.imageMaxBytes / MB)} MB each and .mp4/.mov/.gif video
            up to {Math.round(CREATIVE_IMPORT_LIMITS.videoMaxBytes / MB)} MB each.
          </p>
        </details>
      </div>
    </div>
  )
}
